/**
 * SubirOperativa — sube KML/KMZ/GeoJSON multi-polígono y crea una CAPA OPERATIVA
 * (N predios reales agrupados bajo capa_id, vía POST /v1/capas/operativa →
 * captura_insert_capa_operativa, mig 0094). Evolución de la antigua carga masiva:
 * ahora la subida es una capa gestionable (borrar/deshacer en lote).
 *
 * Mantiene el warning de cautela (Fase 0) + checkbox que habilita el submit:
 * crea predios OPERATIVOS reales mezclados con el inventario.
 */
import { useState } from 'react';
import { bffFetch, invalidateCache } from '@aosenuma/lib-supabase';
import { showToast } from '@aosenuma/ui';
import { parseLayerFile } from '../../captura/parseLayerFile';
import type { SimpleGeometry } from '../../captura/parseUploadedGeoJSON';

const TIPOS = [
  { value: 'PRIVADA', label: 'Privada' },
  { value: 'PARCELA_INDIVIDUAL', label: 'Ejidal (individual)' },
  { value: 'USO_COMUN', label: 'Uso común / comunal' },
  { value: 'ASENTAMIENTO', label: 'Asentamiento' },
  { value: 'VACANTE_NUCLEO_AGRARIO', label: 'Vacante' },
] as const;
type TipoValue = (typeof TIPOS)[number]['value'];

interface RowState {
  geojson_geom: SimpleGeometry;
  geojson_raw: unknown;
  municipio: string;
  nom_nucleo: string;
  num_parcela: string;
  tipo_parcela: TipoValue;
  included: boolean;
}

interface BatchItemResult {
  ok: boolean;
  index: number;
  parcela_id?: string;
  area_ha?: number;
  tipo_parcela?: string;
  error?: string;
  warnings?: unknown[];
}
interface OperativaResult {
  idempotent_hit: boolean;
  capa_id: string;
  total: number;
  created: number;
  failed: number;
  items: BatchItemResult[];
}

function formatoFromName(name: string): 'kml' | 'kmz' | 'geojson' {
  const n = name.toLowerCase();
  if (n.endsWith('.kmz')) return 'kmz';
  if (n.endsWith('.kml')) return 'kml';
  return 'geojson';
}

export function SubirOperativa({ onCreated }: { onCreated?: () => void }) {
  const [fileName, setFileName] = useState('');
  const [nombre, setNombre] = useState('');
  const [rows, setRows] = useState<RowState[]>([]);
  const [parseWarnings, setParseWarnings] = useState<string[]>([]);
  const [loteMunicipio, setLoteMunicipio] = useState('');
  const [loteNucleo, setLoteNucleo] = useState('');
  const [loteTipo, setLoteTipo] = useState<TipoValue>('PRIVADA');
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<OperativaResult | null>(null);
  const [confirmado, setConfirmado] = useState(false);

  async function handleFile(file: File | undefined) {
    if (!file) return;
    setParsing(true);
    setResult(null);
    setConfirmado(false);
    const res = await parseLayerFile(file);
    setParsing(false);
    if (!res.ok) {
      setRows([]);
      setFileName('');
      setParseWarnings([]);
      showToast(res.error, { variant: 'error' });
      return;
    }
    setFileName(file.name);
    if (!nombre.trim()) setNombre(file.name.replace(/\.[^.]+$/, ''));
    setParseWarnings(res.warnings);
    setRows(
      res.items.map((it) => ({
        geojson_geom: it.geojson_geom,
        geojson_raw: it.geojson_raw,
        municipio: loteMunicipio,
        nom_nucleo: loteNucleo,
        num_parcela: (it.nombre ?? '').slice(0, 40),
        tipo_parcela: loteTipo,
        included: true,
      })),
    );
  }

  function applyLoteToAll() {
    setRows((rs) => rs.map((r) => ({ ...r, municipio: loteMunicipio, nom_nucleo: loteNucleo, tipo_parcela: loteTipo })));
    showToast('Defaults del lote aplicados a todas las filas.', { variant: 'info' });
  }

  function updateRow(i: number, patch: Partial<RowState>) {
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  const includedRows = rows.filter((r) => r.included);

  async function submit() {
    if (!nombre.trim()) {
      showToast('Ponle un nombre a la capa.', { variant: 'error' });
      return;
    }
    if (includedRows.length === 0) {
      showToast('No hay predios seleccionados.', { variant: 'error' });
      return;
    }
    if (includedRows.some((r) => !r.municipio.trim())) {
      showToast('Cada predio necesita municipio. Pon el del lote y aplícalo, o llénalo por fila.', { variant: 'error' });
      return;
    }
    const items = includedRows.map((r) => ({
      geojson_geom: r.geojson_geom,
      geojson_raw: r.geojson_raw,
      municipio: r.municipio.trim(),
      nom_nucleo: r.nom_nucleo.trim() || null,
      num_parcela: r.num_parcela.trim() || null,
      tipo_parcela: r.tipo_parcela,
    }));
    const submissionKey = crypto.randomUUID();
    setSubmitting(true);
    try {
      const res = await bffFetch<OperativaResult>('/v1/capas/operativa', {
        method: 'POST',
        body: {
          nombre: nombre.trim(),
          archivo: fileName || null,
          formato: fileName ? formatoFromName(fileName) : null,
          items,
          idempotency_key: submissionKey,
        },
      });
      setResult(res);
      invalidateCache(['capas_registry', 'parcelas_privadas', 'parcelas_publicas']);
      if (res.failed > 0) {
        showToast(`${res.created} creados, ${res.failed} con error. Revisa el detalle.`, { variant: 'error' });
      } else {
        showToast(`Capa "${nombre.trim()}" creada con ${res.created} predio(s).`, { variant: 'success' });
        onCreated?.();
      }
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Error en la carga.', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <p className="panel-sub" style={{ marginTop: 0 }}>
        Cada polígono se vuelve un <strong>predio operativo real</strong> (semáforo, gestión, visitas, consultable por el
        cliente), agrupado bajo esta capa para poder borrarla/deshacerla en lote. El titular se captura después por predio.
      </p>

      <div className="panel-section" style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Nombre de la capa *</label>
          <input value={nombre} maxLength={200} onChange={(e) => setNombre(e.target.value)} placeholder="p. ej. Lote ejidal Huimilpan jun-2026" style={{ width: 320 }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Archivo (.kml, .kmz, .geojson)</label>
          <input
            type="file"
            accept=".kml,.kmz,.geojson,.json,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz,application/geo+json"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; void handleFile(f); }}
            disabled={parsing || submitting}
          />
          {parsing && <span style={{ marginLeft: 8 }}>Procesando…</span>}
          {fileName && !parsing && <span style={{ marginLeft: 8 }}><strong>{fileName}</strong> — {rows.length} polígono(s)</span>}
        </div>
      </div>

      {parseWarnings.length > 0 && (
        <ul style={{ marginTop: 0, marginBottom: 12, color: 'var(--warn, #b56556)' }}>
          {parseWarnings.map((w, i) => <li key={i}>{w}</li>)}
        </ul>
      )}

      {rows.length > 0 && (
        <>
          <div className="panel-section" style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label style={{ display: 'block', fontSize: 13 }}>Municipio (lote)</label>
              <input value={loteMunicipio} maxLength={80} onChange={(e) => setLoteMunicipio(e.target.value)} placeholder="p. ej. Huimilpan" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13 }}>Núcleo agrario (lote, opcional)</label>
              <input value={loteNucleo} maxLength={200} onChange={(e) => setLoteNucleo(e.target.value)} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13 }}>Tipo del lote</label>
              <select value={loteTipo} onChange={(e) => setLoteTipo(e.target.value as TipoValue)}>
                {TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <button type="button" onClick={applyLoteToAll} disabled={submitting}>Aplicar a todas</button>
          </div>

          <div className="panel-section" style={{ overflowX: 'auto', marginBottom: 16 }}>
            <table className="data-table" style={{ width: '100%', fontSize: 13 }}>
              <thead>
                <tr><th>✓</th><th>#</th><th>Etiqueta</th><th>Municipio *</th><th>Núcleo</th><th>Núm. parcela</th><th>Tipo</th></tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} style={{ opacity: r.included ? 1 : 0.45 }}>
                    <td><input type="checkbox" checked={r.included} onChange={(e) => updateRow(i, { included: e.target.checked })} /></td>
                    <td>{i + 1}</td>
                    <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.num_parcela || <em style={{ opacity: 0.6 }}>—</em>}</td>
                    <td><input value={r.municipio} maxLength={80} onChange={(e) => updateRow(i, { municipio: e.target.value })} style={{ width: 120 }} /></td>
                    <td><input value={r.nom_nucleo} maxLength={200} onChange={(e) => updateRow(i, { nom_nucleo: e.target.value })} style={{ width: 120 }} /></td>
                    <td><input value={r.num_parcela} maxLength={40} onChange={(e) => updateRow(i, { num_parcela: e.target.value })} style={{ width: 100 }} /></td>
                    <td>
                      <select value={r.tipo_parcela} onChange={(e) => updateRow(i, { tipo_parcela: e.target.value as TipoValue })}>
                        {TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="panel-section" style={{ marginBottom: 16 }}>
            <div role="alert" style={{ border: '1px solid var(--warn, #b56556)', background: 'rgba(181, 101, 86, 0.08)', borderRadius: 'var(--radius-md, 8px)', padding: '10px 12px', marginBottom: 10, fontSize: 13, lineHeight: 1.45 }}>
              <strong>⚠️ Úsala con cuidado y total conocimiento de causa.</strong> Esto crea{' '}
              <strong>{includedRows.length} predio(s) OPERATIVOS reales</strong> en el inventario (con semáforo, gestión,
              titular, etc.), mezclados con las parcelas existentes — no es para capas de referencia (líneas de transmisión,
              ductos, agua…); para eso usa <strong>Informativa</strong>. Revisa el tipo y los atributos de cada fila.
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, cursor: 'pointer', fontWeight: 600 }}>
                <input type="checkbox" checked={confirmado} onChange={(e) => setConfirmado(e.target.checked)} />
                Entiendo que esto crea predios operativos reales y revisé lo que voy a cargar.
              </label>
            </div>
            <button type="button" onClick={() => void submit()} disabled={submitting || includedRows.length === 0 || !confirmado || !nombre.trim()}>
              {submitting ? 'Cargando…' : `Crear capa con ${includedRows.length} predio(s)`}
            </button>
          </div>
        </>
      )}

      {result && (
        <div className="panel-section">
          <h2 style={{ fontSize: 16 }}>
            Resultado: {result.created} creado(s), {result.failed} con error
            {result.idempotent_hit ? ' (reintento — ya se había procesado)' : ''}
          </h2>
          <ul>
            {result.items.map((it) => (
              <li key={it.index} style={{ color: it.ok ? 'inherit' : 'var(--error, #b56556)' }}>
                #{it.index}:{' '}
                {it.ok
                  ? `✓ ${it.parcela_id} (${it.tipo_parcela}, ${it.area_ha?.toFixed(2)} ha)${it.warnings && it.warnings.length > 0 ? ` — ${it.warnings.length} aviso(s) de solape` : ''}`
                  : `✗ ${it.error}`}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
